import { useCallback, useEffect, useState } from "react";
import { Plus, Pencil, Trash2, ArrowUp, ArrowDown, Loader2, LayoutGrid } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail, resolveImageUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { MediaPicker } from "@/components/admin/MediaPicker";

const EMPTY = { title: "", description: "", image: "", enabled: true };

export default function AdminCategories() {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null | "new" | category
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/categories");
      setCategories(r.data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const openEdit = (cat) => {
    setEditing(cat || "new");
    setForm(cat ? { title: cat.title, description: cat.description || "", image: cat.image || "", enabled: cat.enabled !== false } : EMPTY);
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editing === "new") {
        await api.post("/admin/categories", form);
        toast.success("Category created");
      } else {
        await api.put(`/admin/categories/${editing.id}`, form);
        toast.success("Category updated");
      }
      setEditing(null);
      fetchAll();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (cat) => {
    try {
      await api.delete(`/admin/categories/${cat.id}`);
      toast.success("Category deleted");
      fetchAll();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const toggleEnabled = async (cat) => {
    try {
      await api.put(`/admin/categories/${cat.id}`, { title: cat.title, description: cat.description || "", image: cat.image || "", enabled: !cat.enabled });
      fetchAll();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const move = async (index, dir) => {
    const next = [...categories];
    const j = index + dir;
    if (j < 0 || j >= next.length) return;
    [next[index], next[j]] = [next[j], next[index]];
    setCategories(next);
    try {
      await api.post("/admin/categories/reorder", { ordered_ids: next.map((c) => c.id) });
    } catch {
      toast.error("Failed to save order");
      fetchAll();
    }
  };

  return (
    <div data-testid="admin-categories">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="font-display text-2xl font-black uppercase text-zinc-900">Categories</h1>
          <p className="text-sm text-zinc-500">Vehicle categories shown across the public website.</p>
        </div>
        <Button onClick={() => openEdit(null)} className="bg-[#FF6B00] hover:bg-[#FF8533] text-black font-bold uppercase tracking-wider rounded-sm" data-testid="add-category-button">
          <Plus className="h-4 w-4 mr-2" /> Add Category
        </Button>
      </div>

      {loading ? (
        <div className="flex justify-center py-24"><Loader2 className="h-8 w-8 animate-spin text-[#FF6B00]" /></div>
      ) : (
        <div className="space-y-3">
          {categories.map((c, i) => (
            <div key={c.id} className="bg-white border border-zinc-200 p-4 flex flex-wrap items-center gap-4" data-testid={`category-row-${c.slug}`}>
              <div className="flex flex-col">
                <button onClick={() => move(i, -1)} disabled={i === 0} className="text-zinc-500 hover:text-[#FF6B00] disabled:opacity-30" aria-label={`Move ${c.title} up`} data-testid={`category-up-${i}`}>
                  <ArrowUp className="h-4 w-4" />
                </button>
                <button onClick={() => move(i, 1)} disabled={i === categories.length - 1} className="text-zinc-500 hover:text-[#FF6B00] disabled:opacity-30" aria-label={`Move ${c.title} down`} data-testid={`category-down-${i}`}>
                  <ArrowDown className="h-4 w-4" />
                </button>
              </div>
              <div className="w-20 h-14 bg-zinc-100 rounded-sm overflow-hidden shrink-0 flex items-center justify-center">
                {c.image ? <img src={resolveImageUrl(c.image)} alt="" className="w-full h-full object-cover" /> : <LayoutGrid className="h-5 w-5 text-zinc-500" />}
              </div>
              <div className="flex-1 min-w-[160px]">
                <p className="font-bold text-zinc-900">{c.title}</p>
                <p className="text-xs text-zinc-500 font-mono">{c.slug} · {c.vehicle_count ?? 0} vehicle(s)</p>
              </div>
              <label className="flex items-center gap-2 text-xs text-zinc-500 cursor-pointer">
                <input type="checkbox" checked={c.enabled !== false} onChange={() => toggleEnabled(c)} className="accent-[#FF6B00] h-4 w-4" data-testid={`category-enabled-${c.slug}`} />
                Visible
              </label>
              <div className="flex gap-2">
                <button onClick={() => openEdit(c)} className="p-2 border border-zinc-300 rounded-sm text-zinc-500 hover:text-[#FF6B00] hover:border-[#FF6B00]" aria-label={`Edit ${c.title}`} data-testid={`edit-category-${c.slug}`}>
                  <Pencil className="h-3.5 w-3.5" />
                </button>
                <button onClick={() => remove(c)} className="p-2 border border-zinc-300 rounded-sm text-zinc-500 hover:text-red-600 hover:border-red-500" aria-label={`Delete ${c.title}`} data-testid={`delete-category-${c.slug}`}>
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </div>
            </div>
          ))}
          {categories.length === 0 && (
            <div className="border border-dashed border-zinc-200 p-14 text-center text-zinc-500" data-testid="categories-empty">
              No categories yet.
            </div>
          )}
        </div>
      )}

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="bg-white border-zinc-200 text-zinc-900 max-w-lg" data-testid="category-form-dialog">
          <DialogHeader>
            <DialogTitle className="font-display uppercase text-zinc-900">{editing === "new" ? "Add Category" : "Edit Category"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={save} className="space-y-4 mt-2">
            <div>
              <Label className="text-xs font-mono uppercase tracking-wider text-zinc-500">Title *</Label>
              <Input required value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} className="bg-white border-zinc-300 text-zinc-800 rounded-sm" data-testid="category-title-input" />
            </div>
            <div>
              <Label className="text-xs font-mono uppercase tracking-wider text-zinc-500">Description</Label>
              <Textarea value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} className="bg-white border-zinc-300 text-zinc-800 rounded-sm min-h-[70px]" data-testid="category-description-input" />
            </div>
            <div>
              <Label className="text-xs font-mono uppercase tracking-wider text-zinc-500 block mb-1.5">Image</Label>
              <MediaPicker value={form.image} onChange={(v) => setForm((f) => ({ ...f, image: v }))} testid="category-image" />
            </div>
            <label className="flex items-center gap-2 text-sm text-zinc-600 cursor-pointer">
              <input type="checkbox" checked={form.enabled} onChange={(e) => setForm((f) => ({ ...f, enabled: e.target.checked }))} className="accent-[#FF6B00] h-4 w-4" data-testid="category-enabled-checkbox" />
              Visible on public website
            </label>
            <Button type="submit" disabled={saving} className="w-full h-11 bg-[#FF6B00] hover:bg-[#FF8533] text-black font-bold uppercase tracking-wider rounded-sm" data-testid="category-save-button">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save Category"}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
